// Plain-text diagnostics report (IM-2465). Output is deliberately ASCII-only
// (no smart quotes, em-dashes or other Unicode) so it survives a paste into
// Slack, a terminal, or a ticket unchanged. All timestamps are UTC.

import type { CheckResult, CheckStatus } from './diagnostics-types';

export interface ReportMeta {
  generatedAt: string;
  environment: string;
  appBuild: string;
  restBase: string;
  wsBase: string;
  verdict: string;
}

const STATUS_WORD: Record<CheckStatus, string> = {
  pass: 'PASS',
  warn: 'WARN',
  fail: 'FAIL',
};

const RULE = '='.repeat(60);

const fmtMs = (ms: number | null): string => (ms == null ? 'n/a' : `${ms} ms`);

export function buildReportText(results: CheckResult[], meta: ReportMeta): string {
  const lines: string[] = [];
  lines.push('IMMERSIVE DIAGNOSTICS REPORT');
  lines.push(RULE);
  lines.push(`Generated (UTC): ${meta.generatedAt}`);
  lines.push(`Environment:     ${meta.environment}`);
  lines.push(`App build:       ${meta.appBuild}`);
  lines.push(`REST backend:    ${meta.restBase}`);
  lines.push(`WS backend:      ${meta.wsBase || '(not configured)'}`);
  lines.push(`Likely cause:    ${meta.verdict}`);
  lines.push('');

  const summary = results.map((r) => `${STATUS_WORD[r.status]} ${r.title}`).join(' | ');
  lines.push(`Overall: ${summary}`);
  lines.push('');

  results.forEach((r, i) => {
    lines.push(`[${i + 1}] ${r.title}  (${STATUS_WORD[r.status]})`);
    lines.push(`    ${r.summary}`);
    r.details.forEach((d) => {
      lines.push(`    - ${d.label}: ${d.value}`);
    });
    if (r.timings.length > 0) {
      lines.push('    Timings:');
      r.timings.forEach((t) => {
        lines.push(`      - ${t.label}: ${fmtMs(t.ms)}`);
      });
    }
    lines.push('');
  });

  lines.push(RULE);
  lines.push('End of report.');
  return lines.join('\n');
}

// Writes text to the clipboard. Prefers the async Clipboard API and falls back
// to a hidden textarea + execCommand('copy') when it is unavailable (older
// browsers, or a non-secure context). Returns whether the copy succeeded.
export async function copyReportToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Clipboard API present but blocked (permissions/insecure context) — fall
    // through to the legacy path below.
  }
  return legacyCopy(text);
}

function legacyCopy(text: string): boolean {
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-1000px';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}
